import { WaveRegionPlayIcon } from '@/components/Icons';
import { BezierEaseIcon } from '@/components/Icons/bezier/BezierEaseIcon';
import { BezierEaseInIcon } from '@/components/Icons/bezier/BezierEaseInIcon';
import { BezierEaseInOutIcon } from '@/components/Icons/bezier/BezierEaseInOutIcon';
import { BezierEaseOutIcon } from '@/components/Icons/bezier/BezierEaseOutIcon';
import { BezierLinearIcon } from '@/components/Icons/bezier/BezierLinearIcon';
import { BezierEnum, EditorOperatorEnum } from '@/state/editor-store';
import { displayDuration } from '@/utils';
import Hls from 'hls.js';
import { ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { WaveSurferOptions } from 'wavesurfer.js';
import RegionsPlugin, { RegionParams } from 'wavesurfer.js/dist/plugins/regions.esm.js';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline.esm.js';
import { AudioPlayer, IAudioPlayer } from './AudioPlayer';
import { writeString } from './util';
import { isEmpty } from 'lodash-es';

const baseWaveSurferConfig = {
  container: '#audio-editor-waveform',
  height: 180,
  barRadius: 0,
  normalize: true,
  cursorColor: '#FF5B27',
  cursorWidth: 1.5,
  progressColor: '#FF5B27',
  waveColor: '#8D9092',
  fillParent: true,
  responsive: true,
  minPxPerSec: 1,
  maxCanvasWidth: 4000,
  mediaControls: false,
  autoplay: false,
  interact: true,
  dragToSeek: true,
  sampleRate: 8000,
  autoScroll: false,
};

export interface IAudioEditor extends IAudioPlayer {
  audio: HTMLAudioElement;
  sourceUrl: string | undefined;
  audioProcessCallback: ((currentTime: number) => void) | null;
  hls: Hls | null;
  waveSurferConfig: WaveSurferOptions;
  container: HTMLElement;
  regions: RegionsPlugin;
  currentOperator: EditorOperatorEnum | undefined;
  currentRegion: any | undefined;
  currentFadeOutType: BezierEnum | undefined;
  currentOptions: Partial<WaveSurferOptions>;

  // Basic playback controls
  initWaveSurfer(): void;
  resizeWaveSurfer(): void;
  play(): Promise<void>;
  pause(): Promise<void>;
  stop(): Promise<void>;
  seek(time: number): Promise<void>;
  getDuration(): number;
  getCurrentTime(): number;
  isPlaying(): boolean;

  // Audio file operations
  exportAudio(format: string): Promise<Blob>;
  importAudio(file: File): Promise<void>;
  save(format: string): Promise<Blob>;

  // Audio editing operations
  normalize(): Promise<void>;
  fadeIn(duration: number): Promise<void>;
  fadeOut(duration: number): Promise<void>;
  intro(): Promise<void>;
  outro(): Promise<void>;
  trim(startTime: number, endTime: number, setFinalDuration?: (duration: number) => void): Promise<void>;
  refill(startTime: number, endTime: number): Promise<void>;
  extend(): Promise<void>;
  calcFinalDuration(): void;
  resetOperator(): void;
  loop(startTime: number, endTime: number, count: number): Promise<void>;
  reverse(startTime: number, endTime: number): Promise<void>;
  copy(startTime: number, endTime: number): Promise<void>;
  paste(insertTime: number): Promise<void>;
  delete(startTime: number, endTime: number): Promise<void>;
  adjustVolume(startTime: number, endTime: number, volumeChange: number): Promise<void>;
  addEffect(effectType: string, startTime: number, endTime: number, parameters: object): Promise<void>;

  // Playback rate
  setPlaybackRate(rate: number): void;
  getPlaybackRate(): number;
  playPauseCallback: (() => void) | null;

  // Undo/Redo
  undo(): Promise<void>;
  redo(): Promise<void>;
  getEditHistory(): string[];

  // Callbacks
  setPlayPauseCallback(callback: () => void): void;
  setAudioProcessCallback(callback: (currentTime: number) => void): void;

  // Cleanup
  destroy(): void;

  // Additional methods
  selectEntireTrack(): void;
  addRegion(jsxContent: ReactNode, start: number, end: number): void;

  /**
   * Cut audio segment between startTime and endTime
   */
  cut(startTime: number, endTime: number): Promise<Blob>;
}

export class AudioEditor extends AudioPlayer implements IAudioEditor {
  private static instance: AudioEditor | null = null;
  public regions: RegionsPlugin;
  public playPauseCallback: (() => void) | null = null;
  public currentOperator: EditorOperatorEnum | undefined;
  public currentOptions: Partial<WaveSurferOptions>;
  public currentRegion: any | undefined;
  // private scrollbar: PerfectScrollbar | null = null;
  public currentFadeOutType: BezierEnum | undefined;
  private _currentOperatorDuration: number;
  private _finalDuration: number;

  private _barsBeatsConfig: { bpm?: number; timeOffset?: number; flag?: boolean } | undefined;

  // Getter for currentOperatorDuration
  get currentOperatorDuration(): number {
    return this._currentOperatorDuration;
  }

  // Setter for currentOperatorDuration
  set currentOperatorDuration(value: number) {
    this._currentOperatorDuration = value;
  }

  get finalDuration(): number {
    return this._finalDuration;
  }

  // Setter for currentOperatorDuration
  set finalDuration(value: number) {
    this._currentOperatorDuration = value;
  }

  // Ensure that the play method returns a Promise<void>
  async play(): Promise<void> {
    super.play();
  }

  // Ensure that the pause method returns a Promise<void>
  async pause(): Promise<void> {
    super.pause();
  }

  // Ensure that the stop method returns a Promise<void>
  async stop(): Promise<void> {
    super.stop();
  }
  async seek(time: number): Promise<void> {
    super.seek(time);
  }

  private constructor(
    sourceUrl: string | undefined,
    container: HTMLElement,
    waveSurferConfig: WaveSurferOptions = baseWaveSurferConfig,
    barsBeatsConfig?: { bpm?: number; timeOffset?: number; flag?: boolean },
  ) {
    const { bpm, timeOffset, flag } = barsBeatsConfig ?? {};
    const regionsPlugin = RegionsPlugin.create();
    const beatsPerBar = 4;
    let barsBeatsPlugin;
    if (flag && bpm) {
      const secondsPerBeat = 60 / bpm;
      const secondsPerBeatRounded = Number(secondsPerBeat.toFixed(2));
      const barSeconds = beatsPerBar * secondsPerBeatRounded;

      // console.log('secondsPerBeat', secondsPerBeat, secondsPerBeatRounded);
      barsBeatsPlugin = TimelinePlugin.create({
        height: 20,
        insertPosition: 'beforebegin',
        timeInterval: secondsPerBeatRounded,
        primaryLabelInterval: barSeconds,
        secondaryLabelInterval: barSeconds,
        timeOffset: timeOffset,
        style: {
          fontSize: '8px',
          color: '#fff',
        },
        formatTimeCallback: (time) => {
          // console.log('time', time);
          const beatIndex = Math.round(time / secondsPerBeatRounded);
          const bar = Math.floor(beatIndex / beatsPerBar) + 1;
          const beat = (beatIndex % beatsPerBar) + 1;
          // console.log('beat', beat);
          return `${bar}/${beat}`;
        },
      });
    }

    const configWithPlugins = {
      ...waveSurferConfig,
      container,
      plugins: [
        barsBeatsPlugin,
        TimelinePlugin.create({
          height: 16,
          timeInterval: 0.5,
          primaryLabelInterval: 10,
          secondaryLabelInterval: 1,
          formatTimeCallback: (time) => {
            const duration = this.getDuration();
            if (time % 10 === 0) {
              return displayDuration(time);
            }
            if (Math.round(duration) === time) {
              return displayDuration(duration);
            }
            return '';
          },
        }),
        regionsPlugin,
      ].filter((plugin) => !!plugin),
    };

    super(sourceUrl, container, configWithPlugins);

    // this._bpm = bpm;
    // this._timeOffset = timeOffset;
    this._barsBeatsConfig = barsBeatsConfig;
    this.currentOptions = configWithPlugins;
    this.regions = regionsPlugin;
    this.initRegionsEvents();
    this.initFormatTime();
    this._currentOperatorDuration = 0;
    this._finalDuration = 0;
    const resizeObserver = new ResizeObserver(() => {
      this.resizeWaveSurfer();
    });
    resizeObserver.observe(container);
  }

  resizeWaveSurfer() {
    if (this.wavesurfer) {
      // Force wavesurfer to redraw
      requestAnimationFrame(() => {
        this.wavesurfer?.setOptions({
          container: this.container,
          height: this.currentOptions.height,
          width: this.container.clientWidth,
        });
      });
    }
  }

  selectEntireTrack(): void {
    throw new Error('Method not implemented.');
  }

  private renderReactContent(content: ReactNode): HTMLElement {
    const container = document.createElement('div');
    const root = createRoot(container);
    root.render(content as any);
    return container;
  }

  exportAudio(format: string): Promise<Blob> {
    throw new Error('Method not implemented.');
  }
  importAudio(file: File): Promise<void> {
    throw new Error('Method not implemented.');
  }
  save(format: string): Promise<Blob> {
    throw new Error('Method not implemented.');
  }
  normalize(): Promise<void> {
    throw new Error('Method not implemented.');
  }
  fadeIn(duration: number): Promise<void> {
    throw new Error('Method not implemented.');
  }

  loop(startTime: number, endTime: number, count: number): Promise<void> {
    throw new Error('Method not implemented.');
  }
  reverse(startTime: number, endTime: number): Promise<void> {
    throw new Error('Method not implemented.');
  }
  copy(startTime: number, endTime: number): Promise<void> {
    throw new Error('Method not implemented.');
  }
  paste(insertTime: number): Promise<void> {
    throw new Error('Method not implemented.');
  }
  delete(startTime: number, endTime: number): Promise<void> {
    throw new Error('Method not implemented.');
  }
  adjustVolume(startTime: number, endTime: number, volumeChange: number): Promise<void> {
    throw new Error('Method not implemented.');
  }
  addEffect(effectType: string, startTime: number, endTime: number, parameters: object): Promise<void> {
    throw new Error('Method not implemented.');
  }
  setPlaybackRate(rate: number): void {
    throw new Error('Method not implemented.');
  }
  getPlaybackRate(): number {
    throw new Error('Method not implemented.');
  }
  undo(): Promise<void> {
    throw new Error('Method not implemented.');
  }
  redo(): Promise<void> {
    throw new Error('Method not implemented.');
  }
  getEditHistory(): string[] {
    throw new Error('Method not implemented.');
  }

  private initRegionsEvents(): void {
    if (this.wavesurfer) {
      this.regions.on('region-updated', (region: any) => {
        this.currentRegion = region;
      });

      this.regions.on('region-clicked', (region: any) => {
        this.currentRegion = region;
      });
    }
  }

  private initFormatTime(): void {
    if (this.wavesurfer) {
      const timeEl = document.querySelector('#time');
      const durationEl = document.querySelector('#duration');
      if (!isEmpty(durationEl)) {
        this.wavesurfer.on('decode', (duration) => ((durationEl as HTMLDivElement).textContent = displayDuration(duration)));
      }
      if (!isEmpty(timeEl)) {
        this.wavesurfer.on('timeupdate', (currentTime) => ((timeEl as HTMLDivElement).textContent = displayDuration(currentTime)));
      }
    }
  }

  public static getInstance(
    sourceUrl: string | undefined,
    container: HTMLElement,
    waveSurferConfig: WaveSurferOptions = baseWaveSurferConfig,
    barsBeatsConfig?: { bpm?: number; timeOffset?: number; flag?: boolean },
  ): AudioEditor {
    if (!AudioEditor.instance) {
      AudioEditor.instance = new AudioEditor(sourceUrl, container, waveSurferConfig, barsBeatsConfig);
    }
    return AudioEditor.instance;
  }

  public async intro() {
    this.resetOperator();
    this.currentOperator = EditorOperatorEnum.INTRO;
    this.resizeWaveSurfer();
  }

  public async fadeOut(duration: number) {
    this.regions.clearRegions();
    this.regions.unAll();
    const buttons = [
      {
        icon: <BezierLinearIcon fillColor={this.currentFadeOutType === BezierEnum.LINEAR ? '#eb663a' : '#191b1c'} />,
        name: BezierEnum.LINEAR,
      },
      {
        icon: <BezierEaseInOutIcon fillColor={this.currentFadeOutType === BezierEnum.EASE_IN_OUT ? '#eb663a' : '#191b1c'} />,
        name: BezierEnum.EASE_IN_OUT,
      },
      {
        icon: <BezierEaseIcon fillColor={this.currentFadeOutType === BezierEnum.EASE ? '#eb663a' : '#191b1c'} />,
        name: BezierEnum.EASE,
      },
      {
        icon: <BezierEaseInIcon fillColor={this.currentFadeOutType === BezierEnum.EASE_IN ? '#eb663a' : '#191b1c'} />,
        name: BezierEnum.EASE_IN,
      },
      {
        icon: <BezierEaseOutIcon fillColor={this.currentFadeOutType === BezierEnum.EASE_OUT ? '#eb663a' : '#191b1c'} />,
        name: BezierEnum.EASE_OUT,
      },
    ];

    const fadeOutJsxContent = (
      <div
        className="region-content"
        style={{
          display: 'flex',
          height: '100%',
        }}
      >
        <div
          id="audio-editor-trim-block"
          style={{
            margin: '12px 6px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <button
            style={{
              backgroundColor: '#1e1f21',
              borderRadius: '2px',
              display: 'inline-flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <div
              style={{
                color: '#ff5b27',
                fontSize: '10px',
                padding: '4px 8px',
                fontWeight: 300,
                fontFamily: '"GT Pressura Mono Trial", monospace',
                textTransform: 'uppercase',
              }}
            >
              FADE OUT
            </div>
          </button>
        </div>
      </div>
    );
    const renderButtons = () => {
      this.regions.clearRegions();
      this.addRegion(fadeOutJsxContent, this.audio.duration - duration, this.audio.duration, {
        color: 'rgba(255, 91, 39, 0.3)',
        minLength: 0,
        maxLength: this.audio.duration,
        drag: false,
        resize: false,
      } as RegionParams);
    };

    renderButtons();
    this.currentOperator = EditorOperatorEnum.FADE_OUT;
  }

  public async trim(startTime: number, endTime: number, setFinalDuration?: (duration: number) => void) {
    this.resetOperator();
    const trimJsxContent = (
      <div
        className="region-content"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <WaveRegionPlayIcon
          onClick={(e) => {
            e.stopPropagation();
            const { start, end } = this.regions.getRegions()?.[0] ?? {};
            if (this.isPlaying()) {
              this.pause();
            } else {
              const currentTime = this.getCurrentTime();
              if (currentTime > end || currentTime < start) {
                this.seek(start);
              }
              this.play();
            }
          }}
          style={{
            marginTop: 12,
            marginLeft: 12,
            cursor: 'pointer',
          }}
        />
      </div>
    );
    this.addRegion(trimJsxContent, startTime, endTime, {
      id: 'trim-region',
      color: 'rgba(255, 91, 39, 0.3)',
      minLength: 0,
      maxLength: this.audio.duration,
      drag: true,
      resize: true,
    } as RegionParams);
    this.regions.on('region-update', (region: any, side?: 'start' | 'end') => {
      const duration = region.end - region.start;
      const MIN_DURATION = 1;
      if (duration < MIN_DURATION) {
        if (side === 'start') {
          region.start = region.end - MIN_DURATION;
        } else {
          region.end = region.start + MIN_DURATION;
        }
      }
    });
    this.regions.on('region-updated', (region: any) => {
      if (this._barsBeatsConfig?.bpm && this._barsBeatsConfig?.flag && this._barsBeatsConfig?.timeOffset) {
        const secondsPerBeat = 60 / this._barsBeatsConfig?.bpm;
        const minDuration = 1;
        const newStart =
          Math.round((region.start - this._barsBeatsConfig?.timeOffset) / secondsPerBeat) * secondsPerBeat +
          this._barsBeatsConfig?.timeOffset;
        const newEnd =
          Math.round((region.end - this._barsBeatsConfig?.timeOffset) / secondsPerBeat) * secondsPerBeat +
          this._barsBeatsConfig?.timeOffset;

        if (newEnd - newStart < minDuration) {
          region.setOptions({
            start: newStart,
            end: newStart + minDuration,
          });
        } else {
          region.setOptions({
            start: newStart,
            end: newEnd,
          });
        }
      }

      this._finalDuration = region.end - region.start;
      if (setFinalDuration) {
        setFinalDuration(this._finalDuration);
      }
    });
    this.currentOperator = EditorOperatorEnum.TRIM;
  }

  public async refill(startTime: number, endTime: number) {
    this.resetOperator();
    this.trim(startTime, endTime);
    this.regions.on('region-update', (region: any, side?: 'start' | 'end') => {
      const duration = region.end - region.start;
      // do not change this if you can not understand this
      const MIN_START_END = 10;
      const MIN_DURATION = 5;
      const MAX_DURATION = 30;
      if (duration < MIN_DURATION) {
        if (side === 'start') {
          region.start = region.end - MIN_DURATION;
        } else {
          region.end = region.start + MIN_DURATION;
        }
      } else if (duration > MAX_DURATION) {
        if (side === 'start') {
          region.start = region.end - MAX_DURATION;
        } else {
          region.end = region.start + MAX_DURATION;
        }
      }
      if (region.start < MIN_START_END) {
        region.start = MIN_START_END;
      }
      if (region.end > this.audio.duration - MIN_START_END) {
        if (region.end > this.audio.duration - MIN_START_END && region.end - region.start === MIN_DURATION) {
          region.start = this.audio.duration - (MIN_START_END + MIN_DURATION);
        }
        region.end = this.audio.duration - MIN_START_END;
      }
    });
    this.regions.on('region-updated', (region: any) => {
      this.currentRegion = region;
    });
    this.regions.on('region-clicked', (region: any) => {
      this.currentRegion = region;
    });
    this.currentOperator = EditorOperatorEnum.REFILL;
  }

  public async extend() {
    this.resetOperator();
    this.addRegion(null, 0, this.audio.duration, {
      id: 'extend-region',
      color: 'rgba(255, 91, 39, 0.3)',
      minLength: 0,
      maxLength: this.audio.duration,
      drag: false,
      resize: false,
    } as RegionParams);
    this.currentOperator = EditorOperatorEnum.EXTEND;
  }

  public async outro() {
    this.resetOperator();
    if (this.currentOperator === EditorOperatorEnum.OUTRO) {
      return;
    }
    this.currentOperator = EditorOperatorEnum.OUTRO;
  }

  public destroy(): void {
    if (this.wavesurfer) {
      this.regions.clearRegions();
      this.wavesurfer.destroy();
    }
    AudioEditor.instance = null;
  }

  addRegion(jsxContent: ReactNode, start: number, end: number, options?: RegionParams): void {
    if (!this.wavesurfer) return;

    const contentElement = this.renderReactContent(jsxContent);

    const region = this.regions.addRegion({
      start,
      end,
      content: contentElement,
      ...options,
    });

    const regionElement = document.querySelector(`[part="region region-${region.id}"]`);
    if (regionElement) {
      regionElement.classList.add('striped-region');
    }
  }

  /**
   * Cut audio segment between startTime and endTime
   */
  public async cut(startTime: number, endTime: number): Promise<Blob> {
    if (!this.audio?.src) throw new Error('No audio loaded');
    if (startTime < 0 || endTime > this.audio.duration || startTime >= endTime) {
      throw new Error('Invalid time range');
    }

    // Get the audio data
    const response = await fetch(this.audio.src);
    const arrayBuffer = await response.arrayBuffer();

    // Create audio context
    const audioContext = new AudioContext();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Calculate the frame positions
    const sampleRate = audioBuffer.sampleRate;
    const startFrame = Math.floor(startTime * sampleRate);
    const endFrame = Math.floor(endTime * sampleRate);
    const frameCount = endFrame - startFrame;

    // Create a new buffer for the cut segment
    const newBuffer = audioContext.createBuffer(audioBuffer.numberOfChannels, frameCount, sampleRate);

    // Copy the selected portion of audio data
    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
      const channelData = audioBuffer.getChannelData(channel);
      const newChannelData = newBuffer.getChannelData(channel);
      for (let i = 0; i < frameCount; i++) {
        newChannelData[i] = channelData[startFrame + i];
      }
    }

    // Convert the buffer to a blob
    const offlineContext = new OfflineAudioContext(newBuffer.numberOfChannels, frameCount, sampleRate);
    const source = offlineContext.createBufferSource();
    source.buffer = newBuffer;
    source.connect(offlineContext.destination);
    source.start();

    const renderedBuffer = await offlineContext.startRendering();
    const wavBlob = await this.bufferToWave(renderedBuffer);

    return wavBlob;
  }

  private downloadWav(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private bufferToWave(audioBuffer: AudioBuffer): Promise<Blob> {
    const numOfChan = audioBuffer.numberOfChannels;
    const length = audioBuffer.length * numOfChan * 2;
    const buffer = new ArrayBuffer(44 + length);
    const view = new DataView(buffer);

    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + length, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numOfChan, true);
    view.setUint32(24, audioBuffer.sampleRate, true);
    view.setUint32(28, audioBuffer.sampleRate * 2 * numOfChan, true);
    view.setUint16(32, numOfChan * 2, true);
    view.setUint16(34, 16, true);
    writeString(view, 36, 'data');
    view.setUint32(40, length, true);

    const offset = 44;
    const channelData = new Float32Array(audioBuffer.length);
    let pos = 0;

    for (let i = 0; i < audioBuffer.length; i++) {
      for (let channel = 0; channel < numOfChan; channel++) {
        const channelData = audioBuffer.getChannelData(channel);
        const sample = channelData[i];
        // Clamp samples to [-1, 1] range and convert to 16-bit PCM
        const int16 = Math.max(-1, Math.min(1, sample)) * 0x7fff;
        view.setInt16(pos + offset, int16, true);
        pos += 2;
      }
    }

    return Promise.resolve(new Blob([buffer], { type: 'audio/wav' }));
  }

  public resetOperator(): void {
    this.regions.clearRegions();
    this.regions.unAll();
    this.seek(0);
    this.currentOperator = EditorOperatorEnum.NONE;
  }

  public calcFinalDuration(): number {
    const sourceDuration = this.audio.duration;
    if (this.currentOperator) {
      if ([EditorOperatorEnum.INTRO, EditorOperatorEnum.OUTRO].includes(this.currentOperator)) {
        return sourceDuration + this.currentOperatorDuration;
      }
    }
    if (this.currentOperator === EditorOperatorEnum.TRIM) {
      const { start, end } = this.regions.getRegions()?.[0] ?? {};
      return end - start;
    }
    if (this.currentOperator === EditorOperatorEnum.EXTEND) {
      return this.currentOperatorDuration;
    }
    return sourceDuration;
  }
}
